import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import api from "../lib/api";
import {
  LayoutDashboard, Map, Building2, Network, BadgeCheck, Users,
  ScrollText, Upload, LogOut, Menu, X, Table2, GitPullRequest,
  Inbox, Contact, ClipboardList, CheckSquare, SlidersHorizontal,
  UserSearch, Workflow, CalendarClock, FileSignature, CalendarCheck, ListChecks, FileClock,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, resource: "dashboard", action: "read", end: true },
  { section: "Manpower", resource: "sanctions", action: "read" },
  { to: "/sanctions", label: "Sanction Master", icon: Table2, resource: "sanctions", action: "read" },
  { to: "/scr", label: "Change Requests", icon: GitPullRequest, resource: "scr", action: "read", end: true },
  { to: "/scr/inbox", label: "SCR Approvals", icon: Inbox, resource: "scr", action: "approve" },
  { to: "/employees", label: "Employees", icon: Contact, resource: "employees", action: "read" },
  { to: "/requisitions", label: "Requisitions", icon: ClipboardList, resource: "requisitions", action: "read", end: true },
  { to: "/requisitions/inbox", label: "MRF Approvals", icon: CheckSquare, resource: "requisitions", action: "approve", badge: "mrf" },
  { to: "/requisitions/config", label: "SLA & Rules", icon: SlidersHorizontal, resource: "mrf_config", action: "read" },
  { section: "Hiring", resource: "pipeline", action: "read" },
  { to: "/candidates", label: "Candidates", icon: UserSearch, resource: "candidates", action: "read" },
  { to: "/pipeline", label: "Pipeline", icon: Workflow, resource: "pipeline", action: "read" },
  { to: "/interviews", label: "Interviews", icon: CalendarClock, resource: "pipeline", action: "read" },
  { to: "/offers", label: "Offers", icon: FileSignature, resource: "offers", action: "read" },
  { to: "/joining", label: "Joining Tracker", icon: CalendarCheck, resource: "pipeline", action: "read" },
  { to: "/onboarding", label: "Onboarding", icon: ListChecks, resource: "onboarding", action: "read" },
  { section: "Masters", resource: "masters", action: "read" },
  { to: "/zones", label: "Zones", icon: Map, resource: "masters", action: "read" },
  { to: "/branches", label: "Branches", icon: Building2, resource: "masters", action: "read" },
  { to: "/departments", label: "Departments", icon: Network, resource: "masters", action: "read" },
  { to: "/designations", label: "Designations", icon: BadgeCheck, resource: "masters", action: "read" },
  { section: "Access", resource: "users", action: "read" },
  { to: "/users", label: "Users & Roles", icon: Users, resource: "users", action: "read" },
  { section: "Governance", resource: "audit", action: "read" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, resource: "audit", action: "read" },
  { to: "/reports", label: "Scheduled Reports", icon: FileClock, resource: "reports", action: "read" },
  { to: "/import", label: "Bulk Import", icon: Upload, resource: "import", action: "write" },
];

function NavItems({ can, onNavigate, mrfCount }) {
  return (
    <nav className="flex-1 overflow-y-auto py-4">
      {NAV.map((item, i) => {
        if (!can(item.resource, item.action)) return null;
        if (item.section)
          return (
            <div key={i} className="px-5 pt-5 pb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">
              {item.section}
            </div>
          );
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors duration-200 border-l-2 ${
                isActive
                  ? "border-[#BF9B30] bg-white/10 text-white font-medium"
                  : "border-transparent text-white/70 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Icon size={16} strokeWidth={1.75} />
            {item.label}
            {item.badge === "mrf" && mrfCount > 0 && (
              <span data-testid="mrf-inbox-badge" className="ml-auto bg-[#BB162B] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-sm tabular-nums">{mrfCount}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, can, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mrfCount, setMrfCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!can("requisitions", "approve")) return;
    const load = () => api.get("/mrf/badge").then(({ data }) => setMrfCount(data.pending_with_me)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("mrf-badge-refresh", load);
    return () => { clearInterval(t); window.removeEventListener("mrf-badge-refresh", load); };
  }, [can]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebarInner = (
    <>
      <div className="px-5 py-5 border-b border-white/10">
        <div className="font-serif text-xl text-white tracking-tight">
          HIRE<span className="text-[#BF9B30]">360</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">
          Incheon Motors · Kia
        </div>
      </div>
      <NavItems can={can} onNavigate={() => setMobileOpen(false)} mrfCount={mrfCount} />
      <div className="border-t border-white/10 p-4">
        <div className="text-sm text-white truncate" data-testid="sidebar-user-name">{user?.full_name}</div>
        <div className="text-[10px] uppercase tracking-wider text-[#BF9B30] mt-0.5">{user?.role?.replace(/_/g, " ")}</div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="mt-3 flex items-center gap-2 text-xs text-white/60 hover:text-white transition-colors duration-200"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F5F6F8]">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col bg-[#0E2240]">
        {sidebarInner}
      </aside>

      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-[#0E2240] px-4 py-3">
        <div className="font-serif text-lg text-white">
          HIRE<span className="text-[#BF9B30]">360</span>
        </div>
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-button"
          className="text-white p-1"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
      </header>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[#0E2240] flex flex-col">
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
              className="absolute top-4 right-4 text-white/70"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
            {sidebarInner}
          </div>
        </div>
      )}

      <main className="lg:pl-60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-4 lg:py-6">
          <div className="flex justify-end mb-3">
            <NotificationBell />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
